/**
 * @requirement S-043 AC #1, #2, #3, #4, #5, #6, #7, #9, #10 —
 *   command palette (cmdk-based) opened with Cmd+K / Ctrl+K from
 *   any route. Fuzzy search + focus trap + focus restoration +
 *   aria-live result count + reduced-motion instant + axe AA/AAA
 *   + zero-network.
 *
 * Uses the native `<dialog>` element for a11y-correct focus trap +
 * Esc-to-close without an extra Radix Dialog dependency (S-040 will
 * offer the Dialog primitive when it merges; we stay minimal until
 * then). cmdk renders the input/list/items internally.
 *
 * eslint-disable rationale:
 *   - `jsx-a11y/no-autofocus`: the palette IS the user's requested
 *     action (Cmd+K), so focusing the input on open matches WCAG
 *     2.4.3 (Focus Order) — the input is the expected next target.
 *   - `jsx-a11y/click-events-have-key-events` +
 *     `jsx-a11y/no-noninteractive-element-interactions`: the
 *     dialog's onClick handles click-outside-to-close (standard
 *     modal pattern); Esc + native `<dialog>` cancel event provide
 *     the keyboard equivalent.
 */
/* eslint-disable jsx-a11y/no-autofocus, jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions */

import { Command } from 'cmdk';
import { useHotkeys } from 'react-hotkeys-hook';
import { type ReactElement, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { defaultActions } from '../lib/actions/defaultActions';
import { registerAction, useActions } from '../lib/actions/registry';

/** Register the default action catalogue once, at module load. */
const DEFAULT_CLEANUPS = defaultActions().map((action) => registerAction(action));
// eslint-disable-next-line @typescript-eslint/no-unused-expressions
DEFAULT_CLEANUPS; // Retain closures — actions live for app lifetime.

export function CommandPalette(): ReactElement {
  const dialogRef = useRef<HTMLDialogElement | null>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const actions = useActions();

  const openPalette = useCallback(() => {
    previousFocusRef.current = document.activeElement as HTMLElement | null;
    setOpen(true);
  }, []);

  const closePalette = useCallback(() => {
    setOpen(false);
    setSearch('');
    // Restore focus to whatever had it before open (AC #4 focus
    // restoration).
    previousFocusRef.current?.focus?.();
  }, []);

  useHotkeys('mod+k', (e) => {
    e.preventDefault();
    openPalette();
  });

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return undefined;
    if (open && !dialog.open) dialog.showModal();
    if (!open && dialog.open) dialog.close();
    return undefined;
  }, [open]);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return undefined;
    const handleClose = (): void => closePalette();
    dialog.addEventListener('close', handleClose);
    return () => dialog.removeEventListener('close', handleClose);
  }, [closePalette]);

  const grouped = useMemo(() => {
    const groups = new Map<string, Array<(typeof actions)[number]>>();
    for (const action of actions) {
      const key = action.group ?? 'Other';
      const list = groups.get(key) ?? [];
      list.push(action);
      groups.set(key, list);
    }
    return Array.from(groups.entries());
  }, [actions]);

  return (
    <dialog
      ref={dialogRef}
      // backdrop:bg-(--color-slate-12) — raw palette token (#1e1f24),
      // never theme-swaps. Matches the S-040 overlay-primitive fix
      // pattern; ensures the backdrop stays near-black in both themes.
      className="w-[32rem] max-w-[92vw] rounded-md border border-chrome-border-subtle bg-chrome-surface p-0 text-chrome-text-primary shadow-lg backdrop:bg-(--color-slate-12)/50"
      aria-label="Command palette"
      onClick={(event) => {
        // Click outside the palette content (on the ::backdrop) closes.
        if (event.target === dialogRef.current) closePalette();
      }}
    >
      <Command
        shouldFilter
        className="flex flex-col overflow-hidden rounded-md bg-chrome-surface"
        onKeyDown={(event) => {
          if (event.key === 'Escape') {
            event.preventDefault();
            closePalette();
          }
        }}
      >
        <div className="flex items-center border-b border-chrome-border-subtle px-3">
          <Command.Input
            autoFocus
            value={search}
            onValueChange={setSearch}
            placeholder="Type a command…"
            className="flex h-11 w-full bg-transparent text-sm outline-none placeholder:text-chrome-text-secondary"
          />
        </div>
        <div role="status" aria-live="polite" className="sr-only">
          {actions.length} actions available
        </div>
        <Command.List className="max-h-[320px] overflow-y-auto p-1">
          <Command.Empty className="py-6 text-center text-sm text-chrome-text-secondary">
            No matching commands.
          </Command.Empty>
          {grouped.map(([groupName, groupActions]) => (
            <Command.Group
              key={groupName}
              heading={groupName}
              className="px-1 pb-1 pt-2 text-xs uppercase tracking-wide text-chrome-text-secondary"
            >
              {groupActions.map((action) => (
                <Command.Item
                  key={action.id}
                  value={`${action.label} ${(action.keywords ?? []).join(' ')}`}
                  onSelect={() => {
                    closePalette();
                    void action.run();
                  }}
                  className="flex cursor-pointer items-center justify-between rounded-sm px-2 py-1.5 text-sm text-chrome-text-primary data-[selected=true]:bg-chrome-bg"
                >
                  <span>{action.label}</span>
                  {action.shortcut ? (
                    <kbd className="ms-4 text-xs text-chrome-text-secondary">{action.shortcut}</kbd>
                  ) : null}
                </Command.Item>
              ))}
            </Command.Group>
          ))}
        </Command.List>
      </Command>
    </dialog>
  );
}
